"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { Modal } from "@/components/ui/modal"
import { cn } from "@/lib/utils"

interface DocumentPage {
  id: string
  thumbnailUrl: string
  name: string
}

interface DocumentItem {
  _id: string
  name: string
  description: string
  category: string
  thumbnailUrl: string
  pages: DocumentPage[]
}

interface DocumentPreviewModalProps {
  document: DocumentItem | null
  isOpen: boolean
  onClose: () => void
  onUseTemplate?: (doc: DocumentItem) => void
}

export function DocumentPreviewModal({
  document,
  isOpen,
  onClose,
  onUseTemplate,
}: DocumentPreviewModalProps) {
  const [currentPage, setCurrentPage] = useState(0)

  // Reset page index when document changes
  useEffect(() => {
    setCurrentPage(0)
  }, [document?._id])

  if (!document) return null

  const rawPages = document.pages || []
  const pages = rawPages.length > 0
    ? rawPages
    : [{ id: "p1", thumbnailUrl: document.thumbnailUrl || "/placeholder-template.png", name: "Page 1" }]

  const hasPrev = currentPage > 0
  const hasNext = currentPage < pages.length - 1

  const goToPrev = () => {
    if (hasPrev) setCurrentPage((prev) => prev - 1)
  }

  const goToNext = () => {
    if (hasNext) setCurrentPage((prev) => prev + 1)
  }

  const handleClose = () => {
    setCurrentPage(0)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-5xl w-[95vw] p-0 overflow-hidden flex flex-col shadow-2xl border-white/20 bg-white rounded-2xl md:rounded-3xl"
    >
      <div className="flex flex-col md:flex-row h-[85vh] md:h-[80vh]">
        {/* Left: Preview Area (Focus on A4 Portrait) */}
        <div className="flex-1 bg-slate-100 flex flex-col relative overflow-hidden p-4 md:p-8">
          {/* Header (Inside Preview for spacing) */}
          <div className="absolute top-6 left-6 right-6 z-10 flex items-center justify-between md:hidden">
            <h2 className="text-sm font-bold text-slate-900 truncate pr-4">{document.name}</h2>
          </div>

          <div className="relative flex-1 flex items-center justify-center">
            <div className="relative aspect-[1/1.414] h-full max-h-[70vh] shadow-2xl rounded-sm overflow-hidden bg-white border border-slate-200">
              <AnimatePresence mode="wait">
                <motion.img
                  key={`${document._id}-${currentPage}`}
                  src={pages[currentPage].thumbnailUrl}
                  alt={`Page ${currentPage + 1}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>

              {/* Page indicator pill */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white text-[10px] font-bold">
                {currentPage + 1} / {pages.length}
              </div>
            </div>

            {/* Nav Arrows */}
            {pages.length > 1 && (
              <>
                <button
                  onClick={goToPrev}
                  disabled={!hasPrev}
                  className={cn(
                    "absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-xl border border-slate-100 flex items-center justify-center transition-all cursor-pointer z-20",
                    hasPrev ? "hover:bg-white hover:scale-110" : "opacity-0 pointer-events-none"
                  )}
                >
                  <ChevronLeft className="w-5 h-5 text-slate-700" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={!hasNext}
                  className={cn(
                    "absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-xl border border-slate-100 flex items-center justify-center transition-all cursor-pointer z-20",
                    hasNext ? "hover:bg-white hover:scale-110" : "opacity-0 pointer-events-none"
                  )}
                >
                  <ChevronRight className="w-5 h-5 text-slate-700" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Right: Info Area */}
        <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-slate-100 flex flex-col bg-white">
          <div className="p-6 flex flex-col h-full">
            <div className="mb-6 hidden md:block">
              <span className="inline-block px-2 py-1 rounded-md bg-slate-100 text-slate-500 text-[10px] font-bold mb-2">
                {document.category?.charAt(0).toUpperCase() + document.category?.slice(1)}
              </span>
              <h2 className="text-xl font-bold text-slate-900 leading-tight">
                {document.name}
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto pr-2" style={{ scrollbarWidth: "none" }}>
              <div className="space-y-6">
                <div>
                  <h4 className="text-[11px] font-bold text-slate-400 mb-2">Description</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {document.description}
                  </p>
                </div>

                <div>
                  <h4 className="text-[11px] font-bold text-slate-400 mb-3">Pages ({pages.length})</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {pages.map((page, idx) => (
                      <button
                        key={page.id}
                        onClick={() => setCurrentPage(idx)}
                        className={cn(
                          "relative aspect-[1/1.414] rounded-lg overflow-hidden border-2 transition-all cursor-pointer",
                          idx === currentPage
                            ? "border-blue-600 shadow-md ring-4 ring-blue-50"
                            : "border-slate-100 hover:border-slate-200"
                        )}
                      >
                        <img src={page.thumbnailUrl} className="w-full h-full object-cover" alt="" />
                        <div className="absolute inset-x-0 bottom-0 bg-black/40 py-1 flex items-center justify-center">
                          <span className="text-[9px] font-bold text-white">{page.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100">
              <button
                onClick={() => {
                  onUseTemplate?.(document)
                  handleClose()
                }}
                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-[13px] font-bold transition-all hover:scale-[1.02] active:scale-95 shadow-xl shadow-blue-500/20 cursor-pointer"
              >
                Use This Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
